import { useAuth, UserRole } from "@/features/auth";
import { ReactNode, useEffect } from "react";
import { useNavigate } from "react-router";

/**
 * Props for ProtectedRoute component
 */
interface ProtectedRouteProps {
    /** Child components to render when authenticated */
    children: ReactNode;
    /** Optional array of roles that are allowed to access the route */
    requiredRoles?: UserRole[];
}

/**
 * Route component that requires authentication to access
 * Can also enforce specific roles if provided
 * Redirects to /login if not authenticated, /unauthorized if authenticated but lacking roles
 * @param props Component props
 * @returns Protected route component
 */
export default function ProtectedRoute({ children, requiredRoles }: Readonly<ProtectedRouteProps>) {
    const navigate = useNavigate();
    const { isAuthenticated, isLoading, hasRole, login } = useAuth();

    useEffect(() => {
        // Don't redirect while auth is loading
        if (isLoading) return;

        // Not authenticated - login using msal
        if (!isAuthenticated) {
            // if login was sucessful, will automatically redirect back to the requested page
            login();
        }

        // Authenticated but missing required roles - redirect to unauthorized
        if (requiredRoles && requiredRoles.length > 0) {
            const hasRequiredRole = requiredRoles.some((role) => hasRole(role));
            if (!hasRequiredRole) {
                void navigate("/unauthorized", { replace: true });
            }
        }
    }, [isAuthenticated, isLoading, requiredRoles, hasRole, navigate, login]);

    // Show loading while authentication is being processed
    if (isLoading) return <AuthenticationLoading />;

    // Show loading while not authenticated (login redirect in progress)
    if (!isAuthenticated) return <AuthenticationLoading />;

    // Show loading while checking authorization (redirect may be in progress)
    if (requiredRoles && requiredRoles.length > 0) {
        const hasRequiredRole = requiredRoles.some((role) => hasRole(role));
        if (!hasRequiredRole) {
            return <AuthenticationLoading />;
        }
    }

    // User is authenticated and has required roles
    return <>{children}</>;
}

/**
 * Loading component for authentication processing
 */
const AuthenticationLoading = () => (
    <div className="flex h-screen items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
            <div className="border-primary h-12 w-12 animate-spin rounded-full border-4 border-t-transparent"></div>
            <p className="text-muted-foreground">Verifying authentication...</p>
        </div>
    </div>
);
